/**
 * Schedule Evaluator — determines which slot is active at a given time.
 *
 * Core logic:
 * - Check if manifest is valid (not expired)
 * - For each slot, check if current time falls within its time window, day-of-week, and date range
 * - Select the highest-priority matching slot
 * - If no slot matches, return fallback mode
 */

import type { ScheduleManifest, ScheduleSlot } from '@dooh/shared';

/** Default image display duration in seconds */
export const DEFAULT_IMAGE_DURATION_S = 10;

/** A single item to be played in sequence */
export interface PlaybackItem {
  assetId: string;
  assetType: 'image' | 'video';
  duration: number; // seconds
}

/** Result of evaluating the schedule */
export interface ScheduleEvaluation {
  /** Whether we're playing scheduled content or fallback */
  mode: 'scheduled' | 'fallback';
  /** The matching slot (null in fallback mode) */
  slot: ScheduleSlot | null;
  /** Playlist items to play (empty in fallback mode; caller fills from fallback data) */
  items: PlaybackItem[];
  /** Fallback playlist ID (if in fallback mode or for reference) */
  fallbackPlaylistId: string | null;
}

/**
 * Get the current time components in the specified timezone (#42).
 *
 * Previously the evaluator used the Pi's local clock (getDay/getHours), which
 * is wrong if the device's timezone isn't set correctly or NTP drifts. Now we
 * compute day-of-week, hours, and minutes in the manifest's declared timezone.
 *
 * Returns: { dayOfWeek (ISO 1-7, 1=Monday, 7=Sunday), timeString ("HH:MM"), dateString ("YYYY-MM-DD") }
 */
export function getTimeInTimezone(
  now: Date,
  timezone: string,
): { dayOfWeek: number; timeString: string; dateString: string } {
  try {
    // Use Intl to get parts in the target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short', // "Sun", "Mon", etc.
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const parts = formatter.formatToParts(now);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';

    // ISO 1-7 (Mon-Sun) — the convention used by validation, the schedule
    // compiler, and compiled manifests (#72). The evaluator previously used
    // JS getDay() 0-6 (Sun-Sat), which silently dropped Sunday slots.
    const weekdayMap: Record<string, number> = {
      Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7,
    };
    const dayOfWeek = weekdayMap[get('weekday')] ?? 1;
    const hour = get('hour') === '24' ? '00' : get('hour'); // Intl can return 24 for midnight
    const timeString = `${hour}:${get('minute')}`;
    const dateString = `${get('year')}-${get('month')}-${get('day')}`;

    return { dayOfWeek, timeString, dateString };
  } catch {
    // Invalid timezone — fall back to local time (better than crashing)
    return {
      dayOfWeek: toIsoDayOfWeek(now),
      timeString: formatTime(now),
      dateString: formatDate(now),
    };
  }
}

/**
 * Check if a slot is active at the given time, evaluated in the manifest's timezone.
 *
 * A slot is active when:
 * 1. The current date is within [startDate, endDate]
 * 2. The current day of week is in daysOfWeek
 * 3. The current time is within [startTime, endTime)
 *
 * Supports midnight-crossing slots (e.g., 23:00 to 01:00).
 */
export function isSlotActive(slot: ScheduleSlot, now: Date, timezone?: string): boolean {
  // Get time components in the manifest's timezone (#42)
  const { dayOfWeek, timeString: currentTime, dateString: currentDate } =
    timezone ? getTimeInTimezone(now, timezone) : {
      dayOfWeek: toIsoDayOfWeek(now),
      timeString: formatTime(now),
      dateString: formatDate(now),
    };

  // Check date range
  if (currentDate < slot.startDate || currentDate > slot.endDate) {
    return false;
  }

  // Check day of week (ISO 1-7: 1=Monday, ..., 7=Sunday) (#72)
  if (!slot.daysOfWeek.includes(dayOfWeek)) {
    return false;
  }

  // Check time range
  if (slot.startTime <= slot.endTime) {
    // Normal range (e.g., 06:00-10:00)
    return currentTime >= slot.startTime && currentTime < slot.endTime;
  } else {
    // Midnight-crossing range (e.g., 23:00-01:00)
    return currentTime >= slot.startTime || currentTime < slot.endTime;
  }
}

/**
 * Find the highest-priority matching slot from a manifest.
 * Returns null if no slot matches or if the manifest is expired.
 */
export function findCurrentSlot(
  manifest: ScheduleManifest,
  now: Date,
): ScheduleSlot | null {
  // Check manifest validity
  const currentTime = now.getTime();
  const validFrom = new Date(manifest.validFrom).getTime();
  const validUntil = new Date(manifest.validUntil).getTime();

  if (currentTime < validFrom || currentTime > validUntil) {
    return null;
  }

  if (manifest.slots.length === 0) {
    return null;
  }

  // Find all matching slots, evaluated in the manifest's timezone (#42)
  let bestSlot: ScheduleSlot | null = null;

  for (const slot of manifest.slots) {
    if (!isSlotActive(slot, now, manifest.timezone)) continue;

    // Higher priority wins
    if (!bestSlot || slot.priority > bestSlot.priority) {
      bestSlot = slot;
    }
  }

  return bestSlot;
}

/**
 * Evaluate the full schedule and determine what should be playing.
 *
 * Returns:
 * - scheduled mode with the matching slot and its playback items
 * - fallback mode when no slot matches or manifest is null/expired
 */
export function evaluateSchedule(
  manifest: ScheduleManifest | null,
  now: Date,
): ScheduleEvaluation {
  if (!manifest) {
    return {
      mode: 'fallback',
      slot: null,
      items: [],
      fallbackPlaylistId: null,
    };
  }

  const slot = findCurrentSlot(manifest, now);

  if (!slot) {
    return {
      mode: 'fallback',
      slot: null,
      items: [],
      fallbackPlaylistId: manifest.fallbackPlaylistId,
    };
  }

  return {
    mode: 'scheduled',
    slot,
    items: [], // Caller should resolve via getPlaylistItems with asset info
    fallbackPlaylistId: manifest.fallbackPlaylistId,
  };
}

/**
 * Convert slot asset IDs to PlaybackItem array.
 *
 * Duration precedence, per item:
 *   1. Per-item playlist override (`slot.assetDurations[i]`) when a positive
 *      number — highest priority, lets an operator explicitly shorten/lengthen
 *      any item regardless of its media type.
 *   2. Video native duration from asset metadata.
 *   3. Existing image/loopDuration/default logic (unchanged).
 *
 * `assetDurations` is optional/best-effort: manifests compiled before it
 * existed simply fall through to steps 2–3.
 *
 * @param slot - The schedule slot
 * @param assets - Map of assetId → { type, duration } metadata
 * @param defaultImageDuration - Default duration for images in seconds
 */
export function getPlaylistItems(
  slot: ScheduleSlot,
  assets: Map<string, { type: 'image' | 'video'; duration: number | null }>,
  defaultImageDuration: number = DEFAULT_IMAGE_DURATION_S,
): PlaybackItem[] {
  if (slot.assetIds.length === 0) return [];

  return slot.assetIds.map((assetId, index) => {
    const meta = assets.get(assetId);
    const assetType = meta?.type ?? 'image';
    let duration: number;

    // 1. Per-item playlist override wins when present and positive.
    const override = slot.assetDurations?.[index];

    if (typeof override === 'number' && override > 0) {
      duration = override;
    } else if (assetType === 'video' && meta?.duration) {
      duration = meta.duration;
    } else {
      // For images, distribute loopDuration evenly if multiple assets,
      // or use the full loopDuration for a single asset
      if (slot.assetIds.length === 1 && slot.loopDuration) {
        duration = slot.loopDuration;
      } else if (meta?.duration) {
        duration = meta.duration;
      } else {
        duration = defaultImageDuration;
      }
    }

    return { assetId, assetType, duration };
  });
}

// ─── Helpers ─────────────────────────────────────────────────────────

/** Convert JS getDay() (0=Sunday..6=Saturday) to ISO day-of-week (1=Monday..7=Sunday) */
function toIsoDayOfWeek(d: Date): number {
  const jsDay = d.getDay();
  return jsDay === 0 ? 7 : jsDay;
}

/** Format date as YYYY-MM-DD in local timezone */
function formatDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Format time as HH:mm in local timezone */
function formatTime(d: Date): string {
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}
